import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pacsAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const MODALITY_ICONS = { XRAY: '🩻', CT: '🔍', MRI: '🧲', USG: '🫀', MAMMO: '🔬' };
const STATUS_COLOR = { Pending: '#f97316', Draft: '#eab308', Finalized: '#8b5cf6', Approved: '#22c55e', Rejected: '#ef4444', 'In Progress': '#3b82f6' };

function StatCard({ icon, label, value, color, sub, onClick }) {
    return (
        <div onClick={onClick} style={{ background: 'white', borderRadius: 12, border: `1px solid ${onClick ? '#fca5a5' : '#e2e8f0'}`, padding: '18px 20px', flex: 1, cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s' }}
            onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: color || '#1e293b' }}>{value ?? '—'}</div>
            {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
        </div>
    );
}

export default function RadiologyDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const res = await pacsAPI.getDashboard();
            setData(res.data);
        } catch { toast.error('Failed to load dashboard'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading) return <div style={{ textAlign: 'center', padding: 80, color: '#64748b' }}>Loading dashboard...</div>;
    if (!data) return null;

    const s = data.stats;
    const avgTat = s.avg_tat_minutes
        ? s.avg_tat_minutes >= 60
            ? `${Math.floor(s.avg_tat_minutes / 60)}h ${Math.round(s.avg_tat_minutes % 60)}m`
            : `${s.avg_tat_minutes}m`
        : '—';

    return (
        <div style={{ maxWidth: 1100 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🩻</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>Radiology Dashboard</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Today's summary — {new Date().toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                    </div>
                </div>
                <button onClick={load} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                    🔄 Refresh
                </button>
            </div>

            {/* Top stat cards */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <StatCard icon="📋" label="Today's Orders" value={s.today_total} color="#0369a1" />
                <StatCard icon="⏳" label="Pending" value={s.pending} color="#d97706" />
                <StatCard icon="✅" label="Completed Today" value={s.completed_today} color="#16a34a" />
                <StatCard icon="🚨" label="STAT Pending" value={s.stat_pending} color="#dc2626" sub="Click to view worklist →" onClick={() => navigate('/pacs/worklist')} />
                <StatCard icon="⏱️" label="Avg TAT (Today)" value={avgTat} color="#7c3aed" sub="Order → Approval" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                {/* Today by modality */}
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Today by Modality</h3>
                    {data.by_modality.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: 13 }}>No orders today</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {data.by_modality.map(m => {
                                const pct = s.today_total > 0 ? Math.round((m.count / s.today_total) * 100) : 0;
                                return (
                                    <div key={m.modality}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{MODALITY_ICONS[m.modality] || '🔬'} {m.modality}</span>
                                            <span style={{ fontSize: 12, color: '#64748b' }}>{m.count} total · {m.pending} pending</span>
                                        </div>
                                        <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                                            <div style={{ background: '#0369a1', borderRadius: 4, height: 8, width: `${pct}%`, transition: 'width 0.4s' }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Quick modality counts */}
                    <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                        {[
                            { key: 'today_xray', label: 'X-Ray', icon: '🩻' },
                            { key: 'today_ct', label: 'CT', icon: '🔍' },
                            { key: 'today_mri', label: 'MRI', icon: '🧲' },
                            { key: 'today_usg', label: 'USG', icon: '🫀' },
                        ].map(({ key, label, icon }) => (
                            <div key={key} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
                                <div style={{ fontSize: 16 }}>{icon}</div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#0369a1' }}>{s[key] || 0}</div>
                                <div style={{ fontSize: 10, color: '#64748b' }}>{label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* TAT Breakdown (last 7 days) */}
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>TAT by Modality</h3>
                    <p style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>Last 7 days — Order to Approval average</p>
                    {data.tat_breakdown.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: 13 }}>No approved reports in last 7 days</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    {['Modality', 'Avg TAT', 'Completed'].map(h => (
                                        <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', padding: '0 0 10px', textTransform: 'uppercase' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.tat_breakdown.map(t => {
                                    const mins = parseFloat(t.avg_tat_min) || 0;
                                    const tatStr = mins >= 60 ? `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m` : `${mins}m`;
                                    const tatColor = mins > 180 ? '#dc2626' : mins > 60 ? '#d97706' : '#16a34a';
                                    return (
                                        <tr key={t.modality} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '10px 0', fontSize: 13, fontWeight: 600 }}>{MODALITY_ICONS[t.modality] || '🔬'} {t.modality}</td>
                                            <td style={{ padding: '10px 0', fontSize: 13, fontWeight: 700, color: tatColor }}>{tatStr}</td>
                                            <td style={{ padding: '10px 0', fontSize: 12, color: '#64748b' }}>{t.completed} reports</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Radiologist Workload Panel */}
            {data.by_radiologist && data.by_radiologist.length > 0 && (
                <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 20 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>👨‍⚕️ Radiologist Workload — Today</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {data.by_radiologist.map(r => {
                            const total = parseInt(r.total) || 0;
                            const approved = parseInt(r.approved) || 0;
                            const finalized = parseInt(r.finalized) || 0;
                            const draft = parseInt(r.draft) || 0;
                            const pending = parseInt(r.pending) || 0;
                            const maxTotal = Math.max(...data.by_radiologist.map(x => parseInt(x.total) || 0), 1);
                            const pct = Math.round((total / maxTotal) * 100);
                            return (
                                <div key={r.radiologist_name}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>👤 {r.radiologist_name}</span>
                                        <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                                            <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>✅ {approved} Approved</span>
                                            <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>📋 {finalized} Finalized</span>
                                            <span style={{ background: '#fefce8', color: '#a16207', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>✏️ {draft} Draft</span>
                                            <span style={{ background: '#fff7ed', color: '#c2410c', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>⏳ {pending} Pending</span>
                                            <span style={{ fontWeight: 800, color: '#0369a1' }}>{total} Total</span>
                                        </div>
                                    </div>
                                    <div style={{ background: '#f1f5f9', borderRadius: 4, height: 8 }}>
                                        <div style={{ background: '#0369a1', borderRadius: 4, height: 8, width: `${pct}%`, transition: 'width 0.4s' }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Recent orders */}
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Recent Orders</h3>
                {data.recent.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: 13 }}>No orders yet</p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Order No.', 'Patient', 'Modality', 'Priority', 'Time', 'Status'].map(h => (
                                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent.map(o => (
                                <tr key={o.order_number} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 700, color: '#0369a1' }}>{o.order_number}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{o.full_name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.mrn}</div>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 600 }}>{MODALITY_ICONS[o.modality] || ''} {o.modality}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ background: o.priority === 'STAT' ? '#fee2e2' : '#f1f5f9', color: o.priority === 'STAT' ? '#dc2626' : '#475569', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                            {o.priority}
                                        </span>
                                    </td>
                                    <td style={{ padding: '9px 12px', fontSize: 12, color: '#64748b' }}>{new Date(o.order_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}</td>
                                    <td style={{ padding: '9px 12px' }}>
                                        <span style={{ background: (STATUS_COLOR[o.report_status] || '#475569') + '22', color: STATUS_COLOR[o.report_status] || '#475569', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                            {o.report_status || 'Pending'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
